import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/Common/Button';
import ChangePasswordModal from '../../components/Profile/ChangePasswordModal';
import SEO from '../../components/SEO/SEO';
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaCalendarAlt,
    FaShieldAlt,
    FaEdit,
    FaCheck,
    FaTimes,
    FaCamera,
    FaLock,
    FaClock,
    FaUserTie,
    FaCheckCircle,
    FaExclamationCircle,
    FaSignOutAlt,
    FaHistory,
} from 'react-icons/fa';
import styles from './styles/ProfilePage.module.css';

export default function ProfilePage() {
    const { user, logout } = useContext(AuthContext);
    const { userProfile, profileLoading, updateProfile } = useUserProfile();

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    // SUPPRIMÉ: const [avatar, setAvatar] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    // Charger les données du profil
    useEffect(() => {
        if (userProfile) {
            console.log('Profil chargé:', userProfile);
            setFormData({
                name: userProfile.name || '',
                phone: userProfile.phone || '',
            });
        }
    }, [userProfile]);

    // Formater la date (string ISO ou Timestamp)
    const formatDate = (date) => {
        try {
            if (!date) return 'N/A';

            // Si c'est un Timestamp Firestore
            if (date.toDate && typeof date.toDate === 'function') {
                return date.toDate().toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }

            // Si c'est une string ISO
            if (typeof date === 'string') {
                return new Date(date).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }

            // Si c'est un objet Date
            if (date instanceof Date) {
                return date.toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }

            return 'N/A';
        } catch (err) {
            console.error('Erreur formatage date:', err);
            return 'N/A';
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    // MODIFIÉ: Fonction simplifiée sans setAvatar
    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // TODO: Implémenter l'upload d'avatar si nécessaire
            console.log('Fichier sélectionné:', file.name);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        try {
            setLoading(true);

            await updateProfile(formData);

            setSuccess(true);
            setIsEditing(false);

            setTimeout(() => {
                setSuccess(false);
            }, 3000);
        } catch (err) {
            console.error('Erreur:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError(null);
        if (userProfile) {
            setFormData({
                name: userProfile.name || '',
                phone: userProfile.phone || '',
            });
        }
    };

    if (profileLoading) {
        return (
            <Layout>
                <div className={styles.container}>
                    <div className={styles.loadingState}>
                        <div className={styles.spinner}></div>
                        <p>Chargement du profil...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!user) {
        return (
            <>
                <SEO
                    title="Mon Profil | OV Hairstyle"
                    description="Gérez votre profil et vos réservations chez OV Hairstyle."
                    url="https://ov-hairstyle.vercel.app/profile"
                    image="https://ov-hairstyle.vercel.app/images/og-image.jpeg"
                />
                <Layout>
                    <div className={styles.container}>
                        <div className={styles.notConnected}>
                            <div className={styles.notConnectedIcon}>
                                <FaExclamationCircle />
                            </div>
                            <h2>Non connecté</h2>
                            <p>Vous devez être connecté pour accéder à cette page</p>
                        </div>
                    </div>
                </Layout>
            </>
        );
    }

    const isAdmin = userProfile?.role === 'admin';

    return (
        <Layout>
            <div className={styles.profileContainer}>
                {/* Modal Changement Mot de Passe */}
                {showPasswordModal && (
                    <ChangePasswordModal
                        isOpen={showPasswordModal}
                        onClose={() => setShowPasswordModal(false)}
                    />
                )}

                {/* Header */}
                <div className={styles.profileHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.headerIcon}>
                            <FaUser />
                        </div>
                        <div>
                            <h1 className={styles.pageTitle}>Mon Profil</h1>
                            <p className={styles.pageSubtitle}>Gérez vos informations personnelles</p>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className={styles.contentWrapper}>
                    {/* Left Column - Profile Card */}
                    <div className={styles.leftColumn}>
                        {/* Profile Card */}
                        <div className={styles.profileCard}>
                            {/* Avatar Section */}
                            <div className={styles.avatarSection}>
                                <div className={styles.avatarContainer}>
                                    <div className={styles.avatar}>
                                        {user?.email?.charAt(0).toUpperCase()}
                                    </div>
                                    {isEditing && (
                                        <label className={styles.avatarUpload}>
                                            <FaCamera />
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleAvatarChange}
                                            />
                                        </label>
                                    )}
                                </div>
                                <div className={styles.userStatus}>
                                    <h2 className={styles.userName}>{formData.name || 'Utilisateur'}</h2>
                                    <div className={styles.roleInfo}>
                                        {isAdmin ? (
                                            <>
                                                <FaUserTie className={styles.roleIcon} />
                                                <span className={styles.roleLabel}>Administrateur</span>
                                            </>
                                        ) : (
                                            <>
                                                <FaUser className={styles.roleIcon} />
                                                <span className={styles.roleLabel}>Client</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Messages */}
                            {success && (
                                <div className={styles.successMessage}>
                                    <FaCheckCircle className={styles.messageIcon} />
                                    <div>
                                        <strong>Succès</strong>
                                        <p>Profil mis à jour avec succès!</p>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className={styles.errorMessage}>
                                    <FaExclamationCircle className={styles.messageIcon} />
                                    <div>
                                        <strong>Erreur</strong>
                                        <p>{error}</p>
                                    </div>
                                </div>
                            )}

                            {/* Form */}
                            <form onSubmit={handleSubmit} className={styles.form}>
                                {/* Nom */}
                                <div className={styles.formGroup}>
                                    <label htmlFor="name">Nom complet</label>
                                    <div className={styles.inputWrapper}>
                                        <FaUser className={styles.inputIcon} />
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Votre nom complet"
                                            disabled={!isEditing}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Téléphone */}
                                <div className={styles.formGroup}>
                                    <label htmlFor="phone">Téléphone</label>
                                    <div className={styles.inputWrapper}>
                                        <FaPhone className={styles.inputIcon} />
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="Votre numéro de téléphone"
                                            disabled={!isEditing}
                                        />
                                    </div>
                                    <small className={styles.hint}>
                                        Utilisé pour les confirmations WhatsApp
                                    </small>
                                </div>

                                {/* Email (lecture seule) */}
                                <div className={styles.formGroup}>
                                    <label htmlFor="email">Email</label>
                                    <div className={styles.inputWrapper}>
                                        <FaEnvelope className={styles.inputIcon} />
                                        <input
                                            type="email"
                                            id="email"
                                            value={user.email || ''}
                                            disabled
                                            className={styles.disabledInput}
                                        />
                                    </div>
                                    <small className={styles.hint}>
                                        Non modifiable
                                    </small>
                                </div>

                                {/* Buttons */}
                                <div className={styles.formActions}>
                                    {!isEditing ? (
                                        <Button
                                            type="button"
                                            size="large"
                                            onClick={() => setIsEditing(true)}
                                            className={styles.editBtn}
                                        >
                                            <FaEdit /> Modifier le profil
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                type="submit"
                                                size="large"
                                                loading={loading}
                                                disabled={loading}
                                                className={styles.submitBtn}
                                            >
                                                <FaCheck /> Enregistrer
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="large"
                                                onClick={handleCancel}
                                                disabled={loading}
                                                className={styles.cancelBtn}
                                            >
                                                <FaTimes /> Annuler
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Right Column - Info & Stats */}
                    <div className={styles.rightColumn}>
                        {/* Account Info */}
                        <div className={styles.infoCard}>
                            <div className={styles.infoHeader}>
                                <FaShieldAlt className={styles.infoIcon} />
                                <h3>Informations du compte</h3>
                            </div>
                            <div className={styles.infoContent}>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>
                                        <FaEnvelope /> Email
                                    </span>
                                    <span className={styles.infoValue}>{userProfile?.email}</span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>
                                        <FaUserTie /> Rôle
                                    </span>
                                    <span className={`${styles.infoValue} ${styles[isAdmin ? 'admin' : 'client']}`}>
                                        {isAdmin ? 'Administrateur' : 'Client'}
                                    </span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>
                                        <FaCalendarAlt /> Inscription
                                    </span>
                                    <span className={styles.infoValue}>
                                        {formatDate(userProfile?.createdAt)}
                                    </span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>
                                        <FaClock /> Dernière mise à jour
                                    </span>
                                    <span className={styles.infoValue}>
                                        {formatDate(userProfile?.updatedAt)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Security Card */}
                        <div className={styles.securityCard}>
                            <div className={styles.securityHeader}>
                                <FaLock className={styles.securityIcon} />
                                <h3>Sécurité</h3>
                            </div>
                            <div className={styles.securityContent}>
                                <p className={styles.securityText}>
                                    Votre compte est protégé par une authentification sécurisée.
                                </p>
                                <Button
                                    variant="ghost"
                                    size="small"
                                    onClick={() => setShowPasswordModal(true)}
                                    className={styles.changePasswordBtn}
                                >
                                    <FaLock /> Changer le mot de passe
                                </Button>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className={styles.statsCard}>
                            <div className={styles.statsHeader}>
                                <FaHistory className={styles.statsIcon} />
                                <h3>Activité</h3>
                            </div>
                            <div className={styles.statsContent}>
                                <div className={styles.statItem}>
                                    <span className={styles.statLabel}>Réservations</span>
                                    <span className={styles.statValue}>0</span>
                                </div>
                                <div className={styles.statItem}>
                                    <span className={styles.statLabel}>Complétées</span>
                                    <span className={styles.statValue}>0</span>
                                </div>
                            </div>
                        </div>

                        {/* Logout */}
                        <Button
                            variant="danger"
                            size="large"
                            onClick={logout}
                            className={styles.logoutBtn}
                        >
                            <FaSignOutAlt /> Se déconnecter
                        </Button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
